/**
 * Program-scope mount of the DocumentTemplateManager plus a list of
 * generated ProgramDocuments for this program. Each row shows either a
 * Generate or Open + Regenerate pair, with live status derived from the
 * DocumentGenerationJob model (not the overloaded ProgramDocument.status).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FileText, PlayCircle, ExternalLink, Loader2, AlertTriangle } from 'lucide-react'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import DocumentTemplateManager from '../../components/document-templates/DocumentTemplateManager'
import { useUser } from '../../contexts/UserContext'
import {
  ensureProgramDocument,
  generateProgramDocument,
  getActiveGenerationJob,
  GenerationConflictError,
  listProgramDocuments,
  listVisibleTemplates,
  type DocumentGenerationJob,
  type ProgramDocumentBrief,
} from '../../components/document-templates/api'
import { dedupeByKeyForScope } from '../../components/document-templates/cascade'
import type { DocumentTemplate } from '../../components/document-templates/types'


type RowStatus =
  | { label: 'Generating…'; variant: 'in-progress' }
  | { label: 'Partial failure'; variant: 'warning' }
  | { label: 'Failed'; variant: 'destructive' }
  | { label: 'Complete'; variant: 'success' }
  | { label: 'Draft'; variant: 'pending' }
  | null


function rowStatus(
  doc: ProgramDocumentBrief | undefined,
  job: DocumentGenerationJob | null | undefined,
): RowStatus {
  if (!doc) return null
  if (job && (job.status === 'queued' || job.status === 'running')) {
    return { label: 'Generating…', variant: 'in-progress' }
  }
  if (job && job.status === 'partial_failure') {
    return { label: 'Partial failure', variant: 'warning' }
  }
  if (job && job.status === 'failed') {
    return { label: 'Failed', variant: 'destructive' }
  }
  if (doc.status === 'complete') return { label: 'Complete', variant: 'success' }
  return { label: 'Draft', variant: 'pending' }
}


const POLL_INTERVAL_MS = 3000


const ProgramDocumentTemplatesView = () => {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const id = Number(programId)

  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [docs, setDocs] = useState<ProgramDocumentBrief[]>([])
  // Active job (if any) keyed by program_document.id
  const [jobsByDoc, setJobsByDoc] = useState<Record<string, DocumentGenerationJob | null>>({})
  const [loading, setLoading] = useState(true)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(async () => {
    if (!id) return
    setError(null)
    try {
      const [visible, existing] = await Promise.all([
        listVisibleTemplates({
          scope_level: 'program',
          office_id: user?.office?.id || null,
          user_id: user?.id || null,
          program_id: id,
        }),
        listProgramDocuments(id),
      ])
      setTemplates(dedupeByKeyForScope(visible, { level: 'program', ownerId: id }))
      setDocs(existing)

      // Pull active-job state for every existing document in parallel.
      const jobEntries = await Promise.all(
        existing.map(async (d) => {
          try {
            const job = await getActiveGenerationJob(d.id)
            return [d.id, job] as const
          } catch {
            return [d.id, null] as const
          }
        }),
      )
      setJobsByDoc(Object.fromEntries(jobEntries))
    } catch (e: any) {
      setError(e?.message || 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [id, user?.id, user?.office?.id])

  useEffect(() => {
    if (user !== undefined) refresh()
  }, [refresh, user])

  // Poll for active jobs while any doc on this page is generating.
  const anyActive = Object.values(jobsByDoc).some((j) => !!j)
  useEffect(() => {
    if (!anyActive) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    if (!pollRef.current) {
      pollRef.current = setInterval(refresh, POLL_INTERVAL_MS)
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [anyActive, refresh])

  const handleGenerate = async (template: DocumentTemplate) => {
    setBusyKey(template.key)
    setError(null)
    try {
      const pd = await ensureProgramDocument(id, { templateId: template.id })
      const job = await generateProgramDocument(pd.id)
      setJobsByDoc((prev) => ({ ...prev, [pd.id]: job }))
      navigate(`/programs/${id}/documents/${pd.id}`)
    } catch (e: any) {
      if (e instanceof GenerationConflictError) {
        setError(e.message)
      } else {
        setError(e?.message || 'Generate failed')
      }
    } finally {
      setBusyKey(null)
    }
  }

  const handleOpen = async (template: DocumentTemplate, existingId?: string) => {
    if (existingId) {
      navigate(`/programs/${id}/documents/${existingId}`)
      return
    }
    setBusyKey(template.key)
    try {
      const pd = await ensureProgramDocument(id, { templateId: template.id })
      navigate(`/programs/${id}/documents/${pd.id}`)
    } catch (e: any) {
      setError(e?.message || 'Open failed')
    } finally {
      setBusyKey(null)
    }
  }

  if (!id || Number.isNaN(id)) {
    return <div className="p-8 text-muted-foreground">Invalid program.</div>
  }

  const docsByKey = new Map(docs.map((d) => [d.template_key, d]))

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-10">
      <section className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Documents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Documents for this program. Generate a document from any available template; existing documents open in the editor.
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="text-sm text-muted-foreground italic">
            No document templates available for this program yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {templates.map((tpl) => {
              const existing = docsByKey.get(tpl.key)
              const activeJob = existing ? jobsByDoc[existing.id] ?? null : null
              const status = rowStatus(existing, activeJob)
              const busy = busyKey === tpl.key
              const generating = Boolean(activeJob)
              return (
                <li key={tpl.id} className="bg-card border border-border rounded-lg p-4 flex items-start gap-4">
                  <FileText className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{tpl.name}</span>
                      {status && <Badge variant={status.variant}>{status.label}</Badge>}
                      {activeJob && activeJob.total_sections > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {activeJob.completed_section_keys.length}/{activeJob.total_sections}
                          {activeJob.current_section_key ? ` · ${activeJob.current_section_key}` : ''}
                        </span>
                      )}
                      {status?.label === 'Partial failure' && (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">{tpl.key}</div>
                    {tpl.description && (
                      <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{tpl.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {existing ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpen(tpl, existing.id)}
                        disabled={busy}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open
                      </Button>
                    ) : null}
                    <Button
                      variant={existing ? 'secondary' : 'default'}
                      size="sm"
                      onClick={() => handleGenerate(tpl)}
                      disabled={busy || generating}
                    >
                      {busy || generating ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <PlayCircle className="w-4 h-4 mr-1" />
                      )}
                      {existing ? 'Regenerate' : 'Generate'}
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-medium text-foreground">Template library</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Customize templates for this program. Changes here cascade only into this program's generated documents.
          </p>
        </div>
        <DocumentTemplateManager
          scope={{ level: 'program', ownerId: id }}
          title=" "
          subtitle=""
        />
      </section>
    </div>
  )
}

export default ProgramDocumentTemplatesView
